import React, {PureComponent} from 'react';
import {createRoot} from 'react-dom/client';
import {Alert, Snackbar, ThemeProvider} from '@mui/material';
import theme from 'src/theme';

interface Message {
  content: string | React.ReactNode;
  type: 'success' | 'error' | 'warning' | 'info';
  id: string;
  duration?: number;
}

interface State {
  open: boolean;
  msgs: Array<Message>;
}

let messageCallbacks: {
  success?: (content: string | React.ReactNode, duration?: number) => void;
  error?: (content: string | React.ReactNode, duration?: number) => void;
  warning?: (content: string | React.ReactNode, duration?: number) => void;
  info?: (content: string | React.ReactNode, duration?: number) => void;
} = {};
let rootInstance: any;

function init() {
  if (rootInstance) {
    return;
  }

  const container = document.createElement('div');
  document.body.appendChild(container);

  class MessageWrapper extends PureComponent<any, State> {
    constructor(props: any) {
      super(props);
      this.state = {
        open: false,
        msgs: [],
      };

      // Register callbacks on mount
      messageCallbacks.success = this.success.bind(this);
      messageCallbacks.error = this.error.bind(this);
      messageCallbacks.warning = this.warning.bind(this);
      messageCallbacks.info = this.info.bind(this);
    }

    _removeMsg(id: string) {
      const {msgs} = this.state;
      const filtered = msgs.filter(v => v.id !== id);
      this.setState({
        open: filtered.length > 0,
        msgs: filtered,
      });
    }

    _addMsg(msg: Message) {
      const {msgs} = this.state;
      this.setState({
        open: true,
        msgs: [...msgs, msg],
      });

      const close = this._removeMsg.bind(this, msg.id);

      const hideTimeout = setTimeout(() => {
        close();
      }, msg.duration || 3000);

      return function closeManually() {
        clearTimeout(hideTimeout);
        close();
      };
    }

    success(content: string | React.ReactNode, duration?: number) {
      const id = `${Math.random()}`;
      const msg = {
        id,
        type: 'success',
        content,
        duration,
      };
      return this._addMsg(msg as Message);
    }

    error(content: string | React.ReactNode, duration?: number) {
      const id = `${Math.random()}`;
      const msg = {
        id,
        type: 'error',
        content,
        duration,
      };
      return this._addMsg(msg as Message);
    }

    warning(content: string | React.ReactNode, duration?: number) {
      const id = `${Math.random()}`;
      const msg = {
        id,
        type: 'warning',
        content,
        duration,
      };
      return this._addMsg(msg as Message);
    }

    info(content: string | React.ReactNode, duration?: number) {
      const id = `${Math.random()}`;
      const msg = {
        id,
        type: 'info',
        content,
        duration,
      };
      return this._addMsg(msg as Message);
    }

    render() {
      const {open, msgs} = this.state;
      return (
        <ThemeProvider theme={theme}>
          <Snackbar open={open} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
            <div>
              {msgs.map(msg => (
                <Alert
                  key={msg.id}
                  severity={msg.type}
                  elevation={6}
                  variant="filled"
                  style={{
                    marginBottom: 10,
                    paddingTop: 3,
                    paddingBottom: 3,
                    color: '#fff',
                  }}
                >
                  {msg.content}
                </Alert>
              ))}
            </div>
          </Snackbar>
        </ThemeProvider>
      );
    }
  }

  // Use createRoot for React 18 compatibility
  rootInstance = createRoot(container);
  rootInstance.render(<MessageWrapper />);
}

const message = {
  success: function (content: string | React.ReactNode, duration?: number) {
    init();
    return messageCallbacks.success?.(content, duration);
  },
  error: function (content: string | React.ReactNode, duration?: number) {
    init();
    return messageCallbacks.error?.(content, duration);
  },
  warning: function (content: string | React.ReactNode, duration?: number) {
    init();
    return messageCallbacks.warning?.(content, duration);
  },
  info: function (content: string | React.ReactNode, duration?: number) {
    init();
    return messageCallbacks.info?.(content, duration);
  },
};

export default message;
